import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Wallet, Users, HandCoins, Landmark, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, zmw, dt, openFile } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, Note, useLoad, act } from "@/components/common";

const ICON = { investor_deposit: Wallet, village_saving: Users, loan_repayment: HandCoins, village_loan_repayment: Landmark };
const COLOR = { investor_deposit: "bg-sky-100 text-sky-800 ring-sky-300", village_saving: "bg-emerald-100 text-emerald-800 ring-emerald-300",
  loan_repayment: "bg-amber-100 text-amber-900 ring-amber-300", village_loan_repayment: "bg-violet-100 text-violet-800 ring-violet-300" };

function CategoryBadge({ item }) {
  const I = ICON[item.category];
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1 ${COLOR[item.category]}`} data-testid={`pay-cat-${item.id}`}><I className="h-3.5 w-3.5" />{item.category_label}</span>;
}

function ConfirmDialog({ item, onClose, onDone }) {
  const [ok, setOk] = useState(false);
  if (!item) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Confirm this payment?</DialogTitle></DialogHeader>
        <div className="space-y-2 rounded-lg bg-stone-50 p-4 text-sm">
          <CategoryBadge item={item} />
          <div className="pt-2"><b>{item.name}</b> sent <b className="font-mono">{zmw(item.amount_minor)}</b></div>
          <div>Transaction ID: <b className="font-mono">{item.reference}</b></div>
          <div className="text-stone-600">{item.detail}</div>
        </div>
        <p className="text-sm text-stone-600">Open the company MTN/Airtel account (or bank statement) and check that this exact amount and transaction ID arrived.</p>
        <label className="flex items-start gap-2 text-sm font-medium"><Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} data-testid="pay-confirm-check" />I checked — this money is in the company account.</label>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!ok} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pay-confirm-submit"
            onClick={async () => { if (await act(() => api.post(`/staff/payments-inbox/${item.id}/confirm`, { category: item.category, checked: true }), "Payment confirmed")) onDone(); }}>Yes, confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ item, onClose, onDone }) {
  const [reason, setReason] = useState("");
  if (!item) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Not received?</DialogTitle></DialogHeader>
        <p className="text-sm">Tell {item.name} why this {zmw(item.amount_minor)} payment ({item.reference}) cannot be confirmed.</p>
        <Textarea placeholder="e.g. We could not find this transaction ID in our MTN account" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pay-reject-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" disabled={reason.trim().length < 3} data-testid="pay-reject-submit"
            onClick={async () => { if (await act(() => api.post(`/staff/payments-inbox/${item.id}/reject`, { category: item.category, reason }), "Marked as not received")) onDone(); }}>Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayCard({ i, done, onConfirm, onReject }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-stone-200 bg-white p-4 lift sm:flex-row sm:items-center" data-testid={`pay-item-${i.id}`}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2"><CategoryBadge item={i} />{done && <StatusBadge status={i.status} />}</div>
        <div className="text-base"><b>{i.name}</b> · <span className="text-stone-600">{i.detail}</span></div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-600">
          <span>Transaction ID: <b className="font-mono text-stone-900" data-testid={`pay-ref-${i.id}`}>{i.reference}</b></span>
          <span>Sent on {dt(i.date)}</span><span>{i.method === "bank_transfer" ? "Bank" : "Mobile money"}</span>
          {i.proof_file_id && <button className="underline" onClick={() => openFile(i.proof_file_id)}>View screenshot</button>}
        </div>
      </div>
      <div className="font-mono text-2xl font-bold text-brand sm:text-right">{zmw(i.amount_minor)}</div>
      {!done && <div className="flex gap-2">
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onConfirm(i)} data-testid={`pay-confirm-${i.id}`}><CheckCircle2 className="mr-1.5 h-4 w-4" />Money received</Button>
        <Button variant="outline" onClick={() => onReject(i)} data-testid={`pay-reject-${i.id}`}><XCircle className="mr-1.5 h-4 w-4" />Not received</Button>
      </div>}
    </div>
  );
}

export default function Payments() {
  const [sp, setSp] = useSearchParams();
  const type = sp.get("type") || "";
  const [status, setStatus] = useState("pending");
  const { data, reload } = useLoad(`/staff/payments-inbox?status=${status}${type ? `&category=${type}` : ""}`, [type, status]);
  const [c, setC] = useState(null);
  const [r, setR] = useState(null);
  const pick = (k) => setSp(k ? { type: k } : {});
  const total = data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : 0;
  return (
    <div className="space-y-6">
      <PageHeader overline="Money in" title="Confirm payments" explain="Every payment people say they sent, sorted by type. Check the company MTN/Airtel account for the transaction ID, then press 'Money received'. Only then is it added to their account. If you can't find it, press 'Not received' and tell them why." />
      {!data ? <Loading /> : <>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="pay-categories">
          <button onClick={() => pick("")} data-testid="pay-tab-all" className={`rounded-xl border p-4 text-left transition-colors ${!type ? "border-[#0369a1] bg-sky-50 ring-2 ring-[#0369a1]" : "bg-white hover:bg-sky-50"}`}>
            <Inbox className="h-5 w-5 text-[#0369a1]" /><div className="mt-2 text-sm font-semibold">All types</div><div className="font-mono text-2xl font-bold text-brand">{total}</div></button>
          {data.categories.map((k) => { const I = ICON[k.key]; return (
            <button key={k.key} onClick={() => pick(k.key)} data-testid={`pay-tab-${k.key}`} title={k.explain}
              className={`rounded-xl border p-4 text-left transition-colors ${type === k.key ? "border-[#0369a1] bg-sky-50 ring-2 ring-[#0369a1]" : "bg-white hover:bg-sky-50"}`}>
              <I className="h-5 w-5 text-[#0369a1]" /><div className="mt-2 text-sm font-semibold">{k.label}</div><div className="font-mono text-2xl font-bold text-brand">{data.counts[k.key]}</div>
              <div className="mt-1 text-[11px] leading-snug text-stone-500">{k.explain}</div></button>); })}
        </div>
        <div className="flex gap-1 rounded-lg bg-stone-100 p-1 text-sm font-semibold sm:w-fit">
          {[["pending", "Waiting for me"], ["done", "Already handled"]].map(([v, l]) => <button key={v} onClick={() => setStatus(v)} data-testid={`pay-status-${v}`} className={`rounded-md px-4 py-1.5 ${status === v ? "bg-white text-brand shadow-sm" : "text-stone-500"}`}>{l}</button>)}
        </div>
        {status === "pending" && !data.items.length && <Note>Nothing waiting here. New payments appear as soon as someone reports them.</Note>}
        <div className="space-y-3" data-testid="pay-list">
          {data.items.map((i) => <PayCard key={`${i.category}-${i.id}`} i={i} done={status === "done"} onConfirm={setC} onReject={setR} />)}
        </div>
      </>}
      <ConfirmDialog item={c} onClose={() => setC(null)} onDone={() => { setC(null); reload(); }} />
      <RejectDialog item={r} onClose={() => setR(null)} onDone={() => { setR(null); reload(); }} />
    </div>
  );
}
